import {canPharmacistValidate, useTherapySequenceActions} from '@/modules/patients-therapies/hooks'
import {getParameterDefaultValuesNew} from '@/modules/patients/functions'
import {type ParameterSchemaNew} from '@/modules/patients/schemas'
import {usePrescriptionRoles} from '@/shared/hooks/use-permissions'
import {PatientTherapySequenceStatus} from '@cypro/engine/database/schema'
import {type PatientTherapy} from '@cypro/engine/queries'
import {withForm} from '@cypro/ui/integrations/form'
import {AdminValidation} from './admin-validation'
import {DoctorValidation} from './doctor-validation'
import {PharmacistValidation} from './pharmacist-validation'
import {ValidationSummary} from './validation-summary'

const PRE_PRODUCTION_STATUSES = new Set<string>([
  PatientTherapySequenceStatus.enum.pending_approval,
  PatientTherapySequenceStatus.enum.pending_doctor_validation,
  PatientTherapySequenceStatus.enum.validating,
  PatientTherapySequenceStatus.enum.production_planning,
])

interface SequenceValidationPanelsProps {
  existing: PatientTherapy
  // set when the doctor just saved changes to a validated prescription
  editedAfterValidation?: boolean
  // mirrors the Save button state, so Save and validate cannot bypass a blocked save
  isSaveDisabled?: boolean
  saveDisabledReason?: string
  onSaveAndValidate?: (validate: () => Promise<void>) => void
}

export const SequenceValidationPanels = withForm({
  defaultValues: getParameterDefaultValuesNew(false, undefined) as ParameterSchemaNew,
  props: {} as SequenceValidationPanelsProps,
  render: function Render({
    form,
    existing,
    editedAfterValidation,
    isSaveDisabled,
    saveDisabledReason,
    onSaveAndValidate,
  }) {
    const {actor} = useTherapySequenceActions(existing)
    const {canValidateDoctor, canValidatePharmacist} = usePrescriptionRoles()

    if (!PRE_PRODUCTION_STATUSES.has(existing.status ?? '')) return null

    // budget/admin workflow: board members approve or reject the prescription
    // before the doctor validation starts
    if (existing.status === PatientTherapySequenceStatus.enum.pending_approval) {
      return <AdminValidation sequence={existing} />
    }

    // doctor workflow
    if (existing.status === PatientTherapySequenceStatus.enum.pending_doctor_validation) {
      return (
        <DoctorValidation
          form={form}
          sequence={existing}
          changed={editedAfterValidation}
          isSaveDisabled={isSaveDisabled}
          saveDisabledReason={saveDisabledReason}
          onSaveAndValidate={onSaveAndValidate}
        />
      )
    }

    const isFullyValidated = Boolean(existing.pharmacistValidatedAt)

    // pharmacy validation stage (pharmacist branch)
    if (existing.status === PatientTherapySequenceStatus.enum.validating && canPharmacistValidate(actor)) {
      return (
        <PharmacistValidation
          form={form}
          sequence={existing}
          isSaveDisabled={isSaveDisabled}
          saveDisabledReason={saveDisabledReason}
          onSaveAndValidate={onSaveAndValidate}
        />
      )
    }

    // doctor views their own validation; remove reverts to pending_doctor_validation
    if (
      existing.status === PatientTherapySequenceStatus.enum.validating &&
      canValidateDoctor &&
      existing.doctorValidatedAt
    ) {
      return <ValidationSummary sequence={existing} />
    }

    // fully validated; pharmacists can remove their validation (pre-production only)
    if (isFullyValidated && canValidatePharmacist) {
      return <ValidationSummary sequence={existing} />
    }

    return (
      <DoctorValidation
        form={form}
        sequence={existing}
        changed={editedAfterValidation}
        isSaveDisabled={isSaveDisabled}
        saveDisabledReason={saveDisabledReason}
        onSaveAndValidate={onSaveAndValidate}
      />
    )
  },
})
